import { useEffect, useState } from 'react';
import {
  Mail,
  RefreshCw,
  Edit2,
  Save,
  X,
  Send,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  Server,
} from 'lucide-react';

interface EmailTemplate {
  id: string;
  template_key: string;
  template_name: string;
  subject: string;
  body_html: string;
  body_text: string | null;
  variables: string[] | null;
  sample_data: Record<string, string> | null;
  is_active: boolean;
  updated_at: string;
  updated_by: string | null;
}

interface SmtpSettings {
  host: string;
  port: number;
  username: string;
  password: string;
  fromEmail: string;
  fromName: string;
  secure: boolean;
}

export default function AdminEmailTemplates() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selected template state
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit form state
  const [editSubject, setEditSubject] = useState('');
  const [editBodyHtml, setEditBodyHtml] = useState('');
  const [editBodyText, setEditBodyText] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [editSampleData, setEditSampleData] = useState('');

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');

  // Test email state
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // SMTP settings state
  const [showSmtpSettings, setShowSmtpSettings] = useState(false);
  const [smtpSettings, setSmtpSettings] = useState<SmtpSettings | null>(null);
  const [loadingSmtp, setLoadingSmtp] = useState(false);
  const [editingSmtp, setEditingSmtp] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [smtpForm, setSmtpForm] = useState({
    host: '',
    port: '',
    username: '',
    password: '',
    fromEmail: '',
    fromName: '',
    secure: false,
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/email-templates`, {
        credentials: 'include',
      });

      const data = (await response.json()) as any;

      if (data.success) {
        setTemplates(data.templates || []);
      } else {
        setError(data.error || 'Failed to fetch templates');
      }
    } catch (err) {
      console.error('[EmailTemplates] Error:', err);
      setError('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const selectTemplate = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setEditSubject(template.subject);
    setEditBodyHtml(template.body_html);
    setEditBodyText(template.body_text || '');
    setEditIsActive(template.is_active);
    setEditSampleData(
      template.sample_data ? JSON.stringify(template.sample_data, null, 2) : '{}'
    );
    setEditing(false);
    setShowPreview(false);
    setTestResult(null);
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;

    setSaving(true);

    try {
      // Parse sample_data JSON
      let parsedSampleData: Record<string, string> | null = null;
      try {
        parsedSampleData = editSampleData ? JSON.parse(editSampleData) : null;
      } catch {
        alert('Invalid JSON in sample data');
        setSaving(false);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/email-templates/${selectedTemplate.template_key}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            subject: editSubject,
            body_html: editBodyHtml,
            body_text: editBodyText || null,
            sample_data: parsedSampleData,
            is_active: editIsActive,
          }),
        }
      );

      const data = (await response.json()) as any;

      if (data.success) {
        // Update local state
        setSelectedTemplate(data.template);
        setTemplates((prev) =>
          prev.map((t) => (t.template_key === data.template.template_key ? data.template : t))
        );
        setEditing(false);
      } else {
        alert(data.error || 'Failed to save template');
      }
    } catch (err) {
      console.error('[EmailTemplates] Save error:', err);
      alert('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const generatePreview = async () => {
    if (!selectedTemplate) return;

    try {
      // Parse current sample data for preview
      let variables: Record<string, string> = {};
      try {
        variables = editSampleData ? JSON.parse(editSampleData) : {};
      } catch {
        // Use empty if invalid
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/email-templates/${selectedTemplate.template_key}/preview`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ variables }),
        }
      );

      const data = (await response.json()) as any;

      if (data.success) {
        setPreviewSubject(data.preview.subject);
        setPreviewHtml(data.preview.body_html);
        setShowPreview(true);
      } else {
        alert(data.error || 'Failed to generate preview');
      }
    } catch (err) {
      console.error('[EmailTemplates] Preview error:', err);
      alert('Failed to generate preview');
    }
  };

  const handleSendTest = async () => {
    if (!selectedTemplate || !testEmail) return;

    setSendingTest(true);
    setTestResult(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/email-templates/${selectedTemplate.template_key}/test`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ email: testEmail }),
        }
      );

      const data = (await response.json()) as any;

      if (data.success) {
        setTestResult({ success: true, message: data.message || 'Test email sent successfully!' });
      } else {
        setTestResult({ success: false, message: data.error || 'Failed to send test email' });
      }
    } catch (err) {
      console.error('[EmailTemplates] Test send error:', err);
      setTestResult({ success: false, message: 'Failed to send test email' });
    } finally {
      setSendingTest(false);
    }
  };

  const fetchSmtpSettings = async () => {
    setLoadingSmtp(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/email-templates/smtp-settings`,
        {
          credentials: 'include',
        }
      );

      const data = (await response.json()) as any;

      if (data.success) {
        setSmtpSettings(data.settings);
      }
    } catch (err) {
      console.error('[EmailTemplates] SMTP settings error:', err);
    } finally {
      setLoadingSmtp(false);
    }
  };

  const startEditingSmtp = () => {
    if (smtpSettings) {
      setSmtpForm({
        host: smtpSettings.host,
        port: String(smtpSettings.port),
        username: smtpSettings.username,
        password: '', // Don't pre-fill password
        fromEmail: smtpSettings.fromEmail,
        fromName: smtpSettings.fromName,
        secure: smtpSettings.secure,
      });
      setEditingSmtp(true);
    }
  };

  const cancelEditingSmtp = () => {
    setEditingSmtp(false);
    setSmtpForm({
      host: '',
      port: '',
      username: '',
      password: '',
      fromEmail: '',
      fromName: '',
      secure: false,
    });
  };

  const saveSmtpSettings = async () => {
    setSavingSmtp(true);

    try {
      // Map form fields to setting keys
      const settingsToUpdate: { key: string; value: string }[] = [
        { key: 'smtp_host', value: `"${smtpForm.host}"` },
        { key: 'smtp_port', value: `"${smtpForm.port}"` },
        { key: 'smtp_from_email', value: `"${smtpForm.fromEmail}"` },
        { key: 'smtp_from_name', value: `"${smtpForm.fromName}"` },
        { key: 'smtp_secure', value: smtpForm.secure ? 'true' : 'false' },
      ];

      // Only update username if provided
      if (smtpForm.username) {
        settingsToUpdate.push({ key: 'smtp_username', value: `"${smtpForm.username}"` });
      }

      // Only update password if provided (user entered a new one)
      if (smtpForm.password) {
        settingsToUpdate.push({ key: 'smtp_password', value: `"${smtpForm.password}"` });
      }

      // Update each setting
      for (const setting of settingsToUpdate) {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/admin/settings/${setting.key}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ value: setting.value }),
          }
        );

        const data = (await response.json()) as any;
        if (!data.success) {
          throw new Error(`Failed to update ${setting.key}: ${data.error}`);
        }
      }

      // Refresh settings and exit edit mode
      await fetchSmtpSettings();
      setEditingSmtp(false);
    } catch (err) {
      console.error('[EmailTemplates] Save SMTP error:', err);
      alert(err instanceof Error ? err.message : 'Failed to save SMTP settings');
    } finally {
      setSavingSmtp(false);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button
          onClick={fetchTemplates}
          className="mt-2 text-red-600 hover:text-red-800 flex items-center"
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Mail className="w-7 h-7 mr-3 text-blue-600" />
            Email Templates
          </h1>
          <p className="text-gray-600 mt-1">
            Manage email templates for billing notifications
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => {
              setShowSmtpSettings(!showSmtpSettings);
              if (!smtpSettings) fetchSmtpSettings();
            }}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center"
          >
            <Server className="w-4 h-4 mr-2" />
            SMTP Settings
          </button>
          <button
            onClick={fetchTemplates}
            className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* SMTP Settings Panel */}
      {showSmtpSettings && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Server className="w-5 h-5 mr-2 text-gray-600" />
              SMTP Configuration
            </h2>
            {smtpSettings && !editingSmtp && (
              <button
                onClick={startEditingSmtp}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Edit
              </button>
            )}
            {editingSmtp && (
              <div className="flex space-x-2">
                <button
                  onClick={cancelEditingSmtp}
                  className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  <X className="w-4 h-4" />
                </button>
                <button
                  onClick={saveSmtpSettings}
                  disabled={savingSmtp}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  {savingSmtp ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-1" />
                      Save
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
          {loadingSmtp ? (
            <div className="flex justify-center py-4">
              <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : editingSmtp ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <label className="block text-gray-600 mb-1">Host</label>
                <input
                  type="text"
                  value={smtpForm.host}
                  onChange={(e) => setSmtpForm({ ...smtpForm, host: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded font-mono text-sm"
                  placeholder="mail.example.com"
                />
              </div>
              <div>
                <label className="block text-gray-600 mb-1">Port</label>
                <input
                  type="text"
                  value={smtpForm.port}
                  onChange={(e) => setSmtpForm({ ...smtpForm, port: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded font-mono text-sm"
                  placeholder="587"
                />
              </div>
              <div>
                <label className="block text-gray-600 mb-1">From Email</label>
                <input
                  type="email"
                  value={smtpForm.fromEmail}
                  onChange={(e) => setSmtpForm({ ...smtpForm, fromEmail: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  placeholder="noreply@example.com"
                />
              </div>
              <div>
                <label className="block text-gray-600 mb-1">From Name</label>
                <input
                  type="text"
                  value={smtpForm.fromName}
                  onChange={(e) => setSmtpForm({ ...smtpForm, fromName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  placeholder="OAK Billing"
                />
              </div>
              <div>
                <label className="block text-gray-600 mb-1">Username (optional)</label>
                <input
                  type="text"
                  value={smtpForm.username}
                  onChange={(e) => setSmtpForm({ ...smtpForm, username: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded font-mono text-sm"
                  placeholder="smtp-username"
                />
              </div>
              <div>
                <label className="block text-gray-600 mb-1">Password (leave blank to keep current)</label>
                <input
                  type="password"
                  value={smtpForm.password}
                  onChange={(e) => setSmtpForm({ ...smtpForm, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded font-mono text-sm"
                  placeholder="••••••••"
                />
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={smtpForm.secure}
                    onChange={(e) => setSmtpForm({ ...smtpForm, secure: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-gray-700">Use TLS/SSL (secure connection)</span>
                </label>
              </div>
            </div>
          ) : smtpSettings ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Host:</span>
                <p className="font-mono">{smtpSettings.host}</p>
              </div>
              <div>
                <span className="text-gray-500">Port:</span>
                <p className="font-mono">{smtpSettings.port}</p>
              </div>
              <div>
                <span className="text-gray-500">Secure:</span>
                <p>{smtpSettings.secure ? 'Yes (TLS)' : 'No'}</p>
              </div>
              <div>
                <span className="text-gray-500">From Email:</span>
                <p className="font-mono">{smtpSettings.fromEmail}</p>
              </div>
              <div>
                <span className="text-gray-500">From Name:</span>
                <p>{smtpSettings.fromName}</p>
              </div>
              <div>
                <span className="text-gray-500">Username:</span>
                <p className="font-mono">{smtpSettings.username || '(not set)'}</p>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">Failed to load SMTP settings</p>
          )}
          <p className="text-xs text-gray-500 mt-4">
            Note: Emails are sent via Resend API. Configure your Resend API key in Settings (key: resend_api_key). These settings configure the From address.
          </p>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Templates</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {templates.map((template) => (
              <button
                key={template.id}
                onClick={() => selectTemplate(template)}
                className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                  selectedTemplate?.id === template.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">{template.template_name}</span>
                  {template.is_active ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-gray-400" />
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1 font-mono">{template.template_key}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Template Detail / Editor */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 max-h-[calc(100vh-200px)] flex flex-col">
          {selectedTemplate ? (
            <>
              {/* Header - stays fixed at top */}
              <div className="flex-shrink-0 p-4 border-b border-gray-200 flex justify-between items-center">
                <div>
                  <h2 className="font-semibold text-gray-900">{selectedTemplate.template_name}</h2>
                  <p className="text-xs text-gray-500 font-mono">{selectedTemplate.template_key}</p>
                </div>
                <div className="flex space-x-2">
                  {editing ? (
                    <>
                      <button
                        onClick={() => setEditing(false)}
                        className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
                      >
                        {saving ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-1" />
                            Save
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setEditing(true)}
                        className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center"
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </button>
                      <button
                        onClick={generatePreview}
                        className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Preview
                      </button>
                      <button
                        onClick={() => setShowTestModal(true)}
                        className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 flex items-center"
                      >
                        <Send className="w-4 h-4 mr-1" />
                        Test Send
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Scrollable content area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Status */}
                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={editIsActive}
                      onChange={(e) => setEditIsActive(e.target.checked)}
                      disabled={!editing}
                      className="rounded"
                    />
                    <span className="text-sm">Active</span>
                  </label>
                  <span className="text-xs text-gray-500">
                    Last updated: {formatDate(selectedTemplate.updated_at)}
                    {selectedTemplate.updated_by && ` by ${selectedTemplate.updated_by}`}
                  </span>
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={editSubject}
                      onChange={(e) => setEditSubject(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  ) : (
                    <p className="px-3 py-2 bg-gray-50 rounded-lg text-sm">{editSubject}</p>
                  )}
                </div>

                {/* Variables */}
                {selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Available Variables
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {selectedTemplate.variables.map((v) => (
                        <code
                          key={v}
                          className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs"
                        >
                          {`{{${v}}}`}
                        </code>
                      ))}
                    </div>
                  </div>
                )}

                {/* HTML Body */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    HTML Body
                  </label>
                  {editing ? (
                    <textarea
                      value={editBodyHtml}
                      onChange={(e) => setEditBodyHtml(e.target.value)}
                      rows={12}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                    />
                  ) : (
                    <pre className="px-3 py-2 bg-gray-50 rounded-lg text-xs overflow-auto max-h-64 font-mono whitespace-pre-wrap">
                      {editBodyHtml}
                    </pre>
                  )}
                </div>

                {/* Plain Text Body */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plain Text Body (Fallback)
                  </label>
                  {editing ? (
                    <textarea
                      value={editBodyText}
                      onChange={(e) => setEditBodyText(e.target.value)}
                      rows={6}
                      placeholder="Optional plain text version for email clients that don't support HTML"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                    />
                  ) : (
                    <pre className="px-3 py-2 bg-gray-50 rounded-lg text-xs overflow-auto max-h-32 font-mono whitespace-pre-wrap">
                      {editBodyText || '(not set)'}
                    </pre>
                  )}
                </div>

                {/* Sample Data */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sample Data (JSON for preview/testing)
                  </label>
                  {editing ? (
                    <textarea
                      value={editSampleData}
                      onChange={(e) => setEditSampleData(e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                    />
                  ) : (
                    <pre className="px-3 py-2 bg-gray-50 rounded-lg text-xs overflow-auto max-h-24 font-mono">
                      {editSampleData}
                    </pre>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Mail className="w-12 h-12 mb-2" />
              <p>Select a template to view or edit</p>
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-3xl w-full max-h-[80vh] flex flex-col">
            {/* Header - stays fixed at top */}
            <div className="flex-shrink-0 p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="font-semibold text-gray-900">Email Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div>
                <label className="text-xs text-gray-500">Subject:</label>
                <p className="font-medium">{previewSubject}</p>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-auto max-h-96">
                <iframe
                  srcDoc={previewHtml}
                  className="w-full h-96"
                  title="Email Preview"
                  sandbox=""
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Email Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full max-h-[90vh] flex flex-col">
            {/* Header - stays fixed at top */}
            <div className="flex-shrink-0 p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="font-semibold text-gray-900">Send Test Email</h3>
              <button
                onClick={() => {
                  setShowTestModal(false);
                  setTestEmail('');
                  setTestResult(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <p className="text-sm text-gray-600">
                Send a test email using the template's sample data. The subject will be prefixed
                with "[TEST]".
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Email
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              {testResult && (
                <div
                  className={`p-3 rounded-lg flex items-center space-x-2 ${
                    testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}
                >
                  {testResult.success ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <AlertCircle className="w-5 h-5" />
                  )}
                  <span>{testResult.message}</span>
                </div>
              )}
            </div>
            {/* Footer - stays fixed at bottom */}
            <div className="flex-shrink-0 flex justify-end space-x-2 p-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowTestModal(false);
                  setTestEmail('');
                  setTestResult(null);
                }}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSendTest}
                disabled={sendingTest || !testEmail}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {sendingTest ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Test
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
